import { logger } from "@web-src/utils/logger";
import { ComponentType, useCallback, useContext, useId } from "react";
import { SingletonPopoverContext } from "./SingletonPopoverContext";
import { SingletonPopoverState } from "./types";

/**
 * Tiny hook that wraps SingletonPopoverContext to provide a more convenient API.
 * Automatically handles consumer ID generation and simplifies the popover state management.
 */
export const useSingletonPopover = () => {
  const context = useContext(SingletonPopoverContext);

  if (!context._hasContext) {
    logger.error(
      "useSingletonPopover is used outside of SingletonPopoverProvider"
    );
  }

  const consumerId = useId();

  const isPopoverOpen = context.isPopoverOpen(consumerId);

  const openPopover = useCallback(
    // oxlint-disable-next-line typescript/no-explicit-any
    <TComponent extends ComponentType<any>>(
      state: Omit<SingletonPopoverState<TComponent>, "id">
    ) => {
      context.openPopover({ id: consumerId, ...state });
    },
    [consumerId, context]
  );

  return {
    isPopoverOpen,
    openPopover,
    closePopover: context.closePopover,
  };
};
